// src/components/friendship/FriendshipAssessmentResults.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain, Users, Heart, Shield, AlertTriangle, TrendingUp,
  CheckCircle, XCircle, AlertCircle, ChevronRight, Download,
  Share2, RefreshCw, ArrowRight, Sparkles, Target, Zap,
  Award, BookOpen, Gamepad2, BarChart3,
} from 'lucide-react';
import Button from '../shared/Button';
import { Progress } from '../shared/Progress';
import Card from '../shared/Card';
import { useApp } from '../../context/AppContext';
import { useGuide } from '../../context/GuideContext';
import { GuideCommentary } from '../guide';
import { friendshipArchetypes, friendshipDimensions } from '../../data/friendshipAssessmentData';

const FriendshipAssessmentResults = ({ assessmentData, onRetake, onContinue }) => {
  const { setCurrentView } = useApp();
  const [activeTab, setActiveTab] = useState('overview');
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Extract key data from assessment
  const {
    dominantArchetypes = [],
    archetypePercentages = {},
    strengths = [],
    growthAreas = [],
    patterns = [],
    recommendations = [],
    circleHealth = {},
    dimensionScores = {},
    actionSteps = [],
    overallResult = {},
    shareableInsights = {},
  } = assessmentData || {};
  
  // Get pattern type styling
  const getPatternStyle = (type) => {
    switch (type) {
      case 'critical':
        return {
          bg: 'bg-red-500/10',
          border: 'border-red-500/30',
          icon: XCircle,
          iconColor: 'text-red-400',
          titleColor: 'text-red-300',
        };
      case 'warning':
        return {
          bg: 'bg-orange-500/10',
          border: 'border-orange-500/30',
          icon: AlertTriangle,
          iconColor: 'text-orange-400',
          titleColor: 'text-orange-300',
        };
      case 'challenge':
        return {
          bg: 'bg-yellow-500/10',
          border: 'border-yellow-500/30',
          icon: AlertCircle,
          iconColor: 'text-yellow-400',
          titleColor: 'text-yellow-300',
        };
      case 'growth':
        return {
          bg: 'bg-blue-500/10',
          border: 'border-blue-500/30',
          icon: TrendingUp,
          iconColor: 'text-blue-400',
          titleColor: 'text-blue-300',
        };
      case 'strength':
        return {
          bg: 'bg-green-500/10',
          border: 'border-green-500/30',
          icon: CheckCircle,
          iconColor: 'text-green-400',
          titleColor: 'text-green-300',
        };
      default:
        return {
          bg: 'bg-slate-500/10',
          border: 'border-slate-500/30',
          icon: AlertCircle,
          iconColor: 'text-slate-400',
          titleColor: 'text-slate-300',
        };
    }
  };
  
  // Archetype Breakdown Component
  const ArchetypeBreakdown = () => {
    const sortedArchetypes = Object.entries(archetypePercentages)
      .sort((a, b) => b[1] - a[1]);
    
    return (
      <div className="space-y-6">
        {/* Top 3 Detailed */}
        <div className="grid md:grid-cols-3 gap-6">
          {sortedArchetypes.slice(0, 3).map(([archetype, percentage], index) => {
            const data = friendshipArchetypes[archetype];
            const isPrimary = index === 0;
            
            return (
              <motion.div
                key={archetype}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`p-6 h-full ${isPrimary ? 'border-2 border-teal-500/50' : 'border border-white/10'}`}>
                  {isPrimary && (
                    <div className="text-xs text-teal-400 font-semibold mb-2">PRIMARY ARCHETYPE</div>
                  )}
                  
                  <div className="text-center mb-4">
                    <div className="text-5xl mb-2">{data.emoji}</div>
                    <h3 className="text-xl font-bold text-white">{data.name}</h3>
                    <div className="text-3xl font-bold text-teal-400 mt-2">{percentage}%</div>
                  </div>
                  
                  <div className="w-full bg-white/10 rounded-full h-2 mb-4">
                    <div
                      className="bg-gradient-to-r from-teal-500 to-coral-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  
                  <p className="text-sm text-slate-300 mb-4">{data.shortDesc}</p>
                  
                  <div className="space-y-3">
                    <div className="p-3 bg-teal-500/10 rounded-lg border border-teal-500/30">
                      <div className="text-xs text-teal-400 font-semibold mb-1">SUPERPOWER</div>
                      <p className="text-sm text-white">{data.superpower}</p>
                    </div>
                    
                    {isPrimary && (
                      <div className="p-3 bg-coral-500/10 rounded-lg border border-coral-500/30">
                        <div className="text-xs text-coral-300 font-semibold mb-1">GROWTH EDGE</div>
                        <p className="text-sm text-white">{data.growth}</p>
                      </div>
                    )}
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
        
        {/* All Archetypes Bar Chart */}
        <Card className="p-6">
          <h3 className="text-lg font-bold text-white mb-4">Complete Friendship DNA</h3>
          <div className="space-y-3">
            {sortedArchetypes.map(([archetype, percentage]) => {
              const data = friendshipArchetypes[archetype];
              return (
                <div key={archetype} className="flex items-center gap-3">
                  <div className="text-2xl w-8">{data.emoji}</div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-white">{data.name}</span>
                      <span className="text-sm text-teal-300 font-bold">{percentage}%</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-teal-500 to-coral-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    );
  };
  
  // Patterns Component
  const PatternsDisplay = () => {
    const criticalPatterns = patterns.filter(p => p.type === 'critical');
    const warningPatterns = patterns.filter(p => p.type === 'warning');
    const otherPatterns = patterns.filter(p => !['critical', 'warning'].includes(p.type));

    // If no patterns, show a positive message
    if (patterns.length === 0) {
      return (
        <div className="text-center py-12">
          <Card className="p-8 max-w-2xl mx-auto">
            <div className="text-6xl mb-4">✨</div>
            <h3 className="text-2xl font-bold text-white mb-4">Healthy Patterns Detected!</h3>
            <p className="text-lg text-slate-300 mb-4">
              Great news! Your responses show balanced friendship patterns without any concerning trends that need immediate attention.
            </p>
            <p className="text-sm text-teal-300">
              This suggests you have good instincts about friendship dynamics and healthy relationship skills.
            </p>
          </Card>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Critical Patterns */}
        {criticalPatterns.length > 0 && (
          <div>
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-400" />
              Critical Patterns Needing Immediate Attention
            </h3>
            <div className="space-y-4">
              {criticalPatterns.map((pattern, index) => {
                const style = getPatternStyle(pattern.type);
                const Icon = style.icon;
                
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className={`p-6 ${style.bg} ${style.border}`}>
                      <div className="flex items-start gap-4">
                        <Icon className={`w-6 h-6 ${style.iconColor} flex-shrink-0 mt-1`} />
                        <div className="flex-1">
                          <h4 className={`font-bold text-lg mb-2 ${style.titleColor}`}>
                            {pattern.title}
                          </h4>
                          <p className="text-slate-300 mb-3">{pattern.description}</p>
                          
                          <div className="p-3 bg-black/20 rounded-lg mb-3">
                            <div className="text-sm text-orange-300 font-semibold mb-1">IMPACT:</div>
                            <p className="text-sm text-slate-300">{pattern.implication}</p>
                          </div>
                          
                          <div className="p-3 bg-teal-500/10 rounded-lg border border-teal-500/30">
                            <div className="text-sm text-teal-400 font-semibold mb-1">ACTION STEP:</div>
                            <p className="text-sm text-white">{pattern.suggestion}</p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Warning Patterns */}
        {warningPatterns.length > 0 && (
          <div>
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              Warning Patterns to Address
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {warningPatterns.map((pattern, index) => {
                const style = getPatternStyle(pattern.type);
                const Icon = style.icon;
                
                return (
                  <Card key={index} className={`p-4 ${style.bg} ${style.border}`}>
                    <div className="flex items-start gap-3">
                      <Icon className={`w-5 h-5 ${style.iconColor} flex-shrink-0 mt-1`} />
                      <div>
                        <h4 className={`font-bold mb-1 ${style.titleColor}`}>{pattern.title}</h4>
                        <p className="text-sm text-slate-300 mb-2">{pattern.description}</p>
                        <p className="text-xs text-coral-300 italic">💡 {pattern.suggestion}</p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Other Patterns */}
        {otherPatterns.length > 0 && (
          <div>
            <h3 className="text-lg font-bold text-white mb-4">Other Patterns</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {otherPatterns.map((pattern, index) => {
                const style = getPatternStyle(pattern.type);
                const Icon = style.icon;
                
                return (
                  <Card key={index} className={`p-4 ${style.bg} ${style.border}`}>
                    <div className="flex items-start gap-3">
                      <Icon className={`w-5 h-5 ${style.iconColor} flex-shrink-0 mt-1`} />
                      <div>
                        <h4 className={`font-bold mb-1 ${style.titleColor}`}>{pattern.title}</h4>
                        <p className="text-sm text-slate-300">{pattern.description}</p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };
  
  // Strengths & Growth Component
  const StrengthsGrowth = () => {
    return (
      <div className="grid md:grid-cols-2 gap-8">
        {/* Strengths */}
        <div>
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-green-400" />
            Your Friendship Strengths
          </h3>
          <div className="space-y-4">
            {strengths.map((strength, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-4 bg-green-500/10 border border-green-500/30">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-green-300">
                      {strength.name || friendshipDimensions[strength.dimension]?.name || 'Friendship Skill'}
                    </h4>
                    <span className="text-2xl font-bold text-green-400">{strength.score}%</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2 mb-2">
                    <div
                      className="bg-gradient-to-r from-green-500 to-green-400 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${strength.score}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-slate-300">
                    {strength.description || friendshipDimensions[strength.dimension]?.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Growth Areas */}
        <div>
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Target className="w-6 h-6 text-orange-400" />
            Growth Opportunities
          </h3>
          <div className="space-y-4">
            {growthAreas.map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-4 bg-orange-500/10 border border-orange-500/30">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-orange-300">
                      {area.name || friendshipDimensions[area.dimension]?.name || 'Growth Area'}
                    </h4>
                    <span className="text-2xl font-bold text-orange-400">{area.score}%</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2 mb-2">
                    <div
                      className="bg-gradient-to-r from-orange-500 to-orange-400 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${area.score}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-slate-300">
                    {area.description || friendshipDimensions[area.dimension]?.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Guide's take on growth */}
          <GuideCommentary
            context="assessmentGrowth"
            variant="bubble"
            className="mt-6"
          />
        </div>
      </div>
    );
  };

  // Circle Health Component
  const CircleHealthDisplay = () => {
    const getHealthColor = (score) => {
      if (score >= 70) return 'text-green-400';
      if (score >= 40) return 'text-yellow-400';
      return 'text-red-400';
    };
    
    const getHealthMessage = (score) => {
      if (score >= 70) return 'Your friend circle is thriving! 🌟';
      if (score >= 40) return 'Your circle has potential but needs some attention 💛';
      return 'Your friend circle might be causing more stress than support ⚠️';
    };
    
    return (
      <Card className="p-8">
        <h3 className="text-2xl font-bold text-white mb-6 text-center">
          Friend Circle Health Report
        </h3>
        
        <div className="text-center mb-8">
          <div className={`text-7xl font-bold ${getHealthColor(circleHealth.overallHealth)} mb-2`}>
            {circleHealth.overallHealth}%
          </div>
          <div className="w-full bg-white/10 rounded-full h-4 mb-4">
            <div
              className="bg-gradient-to-r from-teal-500 to-coral-500 h-4 rounded-full transition-all duration-500"
              style={{ width: `${circleHealth.overallHealth}%` }}
            ></div>
          </div>
          <p className="text-lg text-slate-300">{getHealthMessage(circleHealth.overallHealth)}</p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-white/5 rounded-lg">
            <div className="text-sm text-slate-400 mb-1">Diversity</div>
            <div className="text-2xl font-bold text-white">{circleHealth.diversityScore}%</div>
          </div>
          <div className="text-center p-4 bg-white/5 rounded-lg">
            <div className="text-sm text-slate-400 mb-1">Balance</div>
            <div className="text-2xl font-bold text-white">{circleHealth.reciprocityBalance}%</div>
          </div>
          <div className="text-center p-4 bg-white/5 rounded-lg">
            <div className="text-sm text-slate-400 mb-1">Conflict Health</div>
            <div className="text-2xl font-bold text-white">{circleHealth.conflictHealth}%</div>
          </div>
          <div className="text-center p-4 bg-white/5 rounded-lg">
            <div className="text-sm text-slate-400 mb-1">Group Cohesion</div>
            <div className="text-2xl font-bold text-white">{circleHealth.groupCohesion}%</div>
          </div>
        </div>
      </Card>
    );
  };
  
  // Recommendations Component
  const RecommendationsDisplay = () => {
    return (
      <div className="space-y-6">
        <h3 className="text-2xl font-bold text-white mb-4">Your Personalised Growth Path</h3>
        
        <div className="grid gap-6">
          {recommendations.map((rec, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className={`p-6 ${
                rec.priority === 'critical' ? 'border-2 border-red-500/50' :
                rec.priority === 'high' ? 'border-2 border-orange-500/50' :
                'border border-white/10'
              }`}>
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    rec.priority === 'critical' ? 'bg-red-500/20' :
                    rec.priority === 'high' ? 'bg-orange-500/20' :
                    'bg-teal-500/20'
                  }`}>
                    <BookOpen className={`w-6 h-6 ${
                      rec.priority === 'critical' ? 'text-red-400' :
                      rec.priority === 'high' ? 'text-orange-400' :
                      'text-teal-400'
                    }`} />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-lg font-bold text-white">{rec.category}</h4>
                      <div className="flex gap-2">
                        {rec.isAccessible && (
                          <span className="px-2 py-1 text-xs rounded-full font-medium bg-green-500/20 text-green-300">
                            AVAILABLE NOW
                          </span>
                        )}
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                          rec.priority === 'critical' ? 'bg-red-500/20 text-red-300' :
                          rec.priority === 'high' ? 'bg-orange-500/20 text-orange-300' :
                          'bg-teal-500/20 text-teal-300'
                        }`}>
                          {rec.priority.toUpperCase()} PRIORITY
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-slate-300 mb-3">{rec.reason}</p>
                    
                    {/* Available Modules */}
                    {rec.modules && rec.modules.length > 0 && (
                      <div className="space-y-2 mb-4">
                        <h5 className="text-sm font-medium text-purple-300">Available Modules:</h5>
                        {rec.modules.map((module, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentView('modules')}
                            className="w-full text-left p-3 bg-slate-700/50 rounded-lg hover:bg-slate-600/50 transition-colors group"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <CheckCircle className="w-4 h-4 text-teal-400" />
                                <div>
                                  <span className="text-white font-medium">{module.title}</span>
                                  <div className="text-xs text-slate-400">{module.category}</div>
                                </div>
                              </div>
                              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-white bg-coral-500/20 px-2 py-1 rounded-full">
                        <Zap className="w-4 h-4 inline mr-1" />
                        {rec.timeframe}
                      </span>
                      <Button
                        size="sm"
                        className="bg-teal-600 hover:bg-teal-700"
                        onClick={() => setCurrentView('modules')}
                      >
                        View All Modules
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    );
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-teal-900 to-slate-900 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="text-6xl mb-4">{shareableInsights.emoji || '✨'}</div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Your Friendship Vibe Check Results
          </h1>
          <p className="text-xl text-teal-300 mb-2">{overallResult.level}</p>
          <p className="text-lg text-slate-300">{overallResult.vibeCheck}</p>
        </motion.div>
        
        {/* Overall Message */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="max-w-4xl mx-auto mb-8"
        >
          <Card className="p-6 bg-gradient-to-r from-teal-500/10 to-coral-500/10 border-teal-500/30">
            <p className="text-lg text-white leading-relaxed">
              {overallResult.message}
            </p>
            {overallResult.honestTalk && (
              <p className="text-coral-300 mt-4 font-medium">
                💡 {overallResult.honestTalk}
              </p>
            )}
          </Card>
        </motion.div>

        {/* Guide Commentary on Results */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="max-w-4xl mx-auto mb-8"
        >
          <GuideCommentary
            context="assessmentCelebrate"
            variant="card"
          />
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {[
            { id: 'overview', label: 'Overview', icon: Brain },
            { id: 'archetypes', label: 'Your DNA', icon: Users },
            { id: 'patterns', label: 'Patterns', icon: AlertTriangle },
            { id: 'strengths', label: 'Strengths & Growth', icon: TrendingUp },
            { id: 'health', label: 'Circle Health', icon: Heart },
            { id: 'next-steps', label: 'Next Steps', icon: Target },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'bg-teal-600 text-white'
                    : 'bg-white/10 text-slate-300 hover:bg-white/20'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
        
        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="max-w-6xl mx-auto"
          >
            {activeTab === 'overview' && (
              <div className="space-y-8">
                {/* Quick Summary */}
                <div className="grid md:grid-cols-3 gap-6">
                  <Card className="p-6 text-center">
                    <div className="text-3xl mb-2">{dominantArchetypes[0]?.details?.emoji}</div>
                    <h3 className="text-lg font-bold text-white mb-2">Primary Archetype</h3>
                    <p className="text-2xl text-teal-400 font-bold">
                      {dominantArchetypes[0]?.details?.name}
                    </p>
                    {/* Six archetypes means an even split is ~17% each, so a
                        "primary" on 21% with the runner-up on 19% is not a
                        result - it is noise wearing a label. Say so rather
                        than let a two-point gap read as a verdict. */}
                    <p className="text-sm text-slate-400 mt-1">{dominantArchetypes[0]?.percentage}%</p>
                    {(dominantArchetypes[0]?.percentage || 0) - (dominantArchetypes[1]?.percentage || 0) < 5 &&
                      dominantArchetypes[1] && (
                      <p className="mt-2 text-xs leading-relaxed text-teal-200/70">
                        Only just ahead of {dominantArchetypes[1]?.details?.name} on{' '}
                        {dominantArchetypes[1]?.percentage}%. You are a genuine mix rather than
                        one type - worth reading both.
                      </p>
                    )}
                  </Card>
                  
                  <Card className="p-6 text-center">
                    <div className="text-3xl mb-2">💪</div>
                    <h3 className="text-lg font-bold text-white mb-2">Top Strength</h3>
                    <p className="text-xl text-green-400 font-bold">
                      {strengths[0]?.name || friendshipDimensions[strengths[0]?.dimension]?.name || 'Friendship Skills'}
                    </p>
                    <p className="text-sm text-slate-400 mt-1">{strengths[0]?.score}% strong</p>
                  </Card>
                  
                  <Card className="p-6 text-center">
                    <div className="text-3xl mb-2">🎯</div>
                    <h3 className="text-lg font-bold text-white mb-2">Key Focus Area</h3>
                    <p className="text-xl text-orange-400 font-bold">
                      {growthAreas[0]?.name || friendshipDimensions[growthAreas[0]?.dimension]?.name || 'Growth Potential'}
                    </p>
                    <p className="text-sm text-slate-400 mt-1">Room to grow</p>
                  </Card>
                </div>
                
                {/* Action Steps */}
                <Card className="p-6">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Sparkles className="w-6 h-6 text-yellow-400" />
                    Your Next Steps
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {actionSteps.map((step, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-white">{step}</p>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}
            
            {activeTab === 'archetypes' && <ArchetypeBreakdown />}
            {activeTab === 'patterns' && <PatternsDisplay />}
            {activeTab === 'strengths' && <StrengthsGrowth />}
            {activeTab === 'health' && <CircleHealthDisplay />}
            {activeTab === 'next-steps' && <RecommendationsDisplay />}
          </motion.div>
        </AnimatePresence>
        
        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex flex-wrap justify-center gap-4 mt-12"
        >
          <Button
            size="lg"
            className="bg-gradient-to-r from-teal-600 to-coral-600 hover:from-teal-700 hover:to-coral-700"
            onClick={onContinue || (() => setCurrentView('dashboard'))}
          >
            <BarChart3 className="w-5 h-5 mr-2" />
            Go to Dashboard
          </Button>
          
          <Button
            size="lg"
            variant="secondary"
            onClick={() => setCurrentView('modules')}
          >
            <BookOpen className="w-5 h-5 mr-2" />
            Start Learning
          </Button>
          
          <Button
            size="lg"
            variant="secondary"
            onClick={() => setShowShareModal(true)}
          >
            <Share2 className="w-5 h-5 mr-2" />
            Share Results
          </Button>
          
          <Button
            size="lg"
            variant="outline"
            onClick={onRetake || (() => setCurrentView('friendship-assessment-intro'))}
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Retake Assessment
          </Button>
        </motion.div>
        
        {/* Share Modal */}
        {showShareModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <Card className="p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-white mb-4">Share Your Friendship DNA</h3>

              <div className="p-4 bg-teal-500/10 rounded-lg border border-teal-500/30 mb-4">
                <p className="text-center text-white mb-2">
                  I'm {shareableInsights.archetypeBlend}
                </p>
                <p className="text-center text-teal-300 text-sm">
                  My friendship superpower: {shareableInsights.superpower}
                </p>
                <p className="text-center text-coral-300 text-sm mt-2">
                  {shareableInsights.level}
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  className="flex-1"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `I took the Friendship Vibe Check! I'm ${shareableInsights.archetypeBlend}. My friendship superpower: ${shareableInsights.superpower}`
                    );
                    setShowShareModal(false);
                  }}
                >
                  Copy Text
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setShowShareModal(false)}
                >
                  Close
                </Button>
              </div>
            </Card>
          </div>
        )}

      </div>
    </div>
  );
};

export default FriendshipAssessmentResults;